import { useMemo } from 'react';
import { useLevelUpStore, WizardStep } from '../stores/levelUpStore';
import {
  getRemainingSlots,
  getWizardCompletionPercentage,
  generateLevelUpSummary
} from '../utils/levelUpCalculations';

/**
 * Custom hook for Level-Up Wizard functionality
 * Provides computed state and convenience methods for wizard components
 */
export function useLevelUpWizard() {
  // Subscribe to store state (these change)
  const session = useLevelUpStore((state) => state.session);
  const currentStep = useLevelUpStore((state) => state.currentStep);
  const character = useLevelUpStore((state) => state.character);
  const isLoading = useLevelUpStore((state) => state.isLoading);
  const errors = useLevelUpStore((state) => state.errors);
  const warnings = useLevelUpStore((state) => state.warnings);

  // Get actions directly (don't subscribe - they never change)
  const initializeSession = useLevelUpStore.getState().initializeSession;
  const loadExistingSession = useLevelUpStore.getState().loadExistingSession;
  const clearStore = useLevelUpStore.getState().clearStore;
  const setCurrentStep = useLevelUpStore.getState().setCurrentStep;
  const goNext = useLevelUpStore.getState().goNext;
  const goBack = useLevelUpStore.getState().goBack;
  const canGoNext = useLevelUpStore.getState().canGoNext;
  const canGoBack = useLevelUpStore.getState().canGoBack;
  const setTierAchievementExperience = useLevelUpStore.getState().setTierAchievementExperience;
  const addAdvancementChoice = useLevelUpStore.getState().addAdvancementChoice;
  const removeAdvancementChoice = useLevelUpStore.getState().removeAdvancementChoice;
  const clearAdvancementChoices = useLevelUpStore.getState().clearAdvancementChoices;
  const setAdditionalDomainCard = useLevelUpStore.getState().setAdditionalDomainCard;
  const setNewDomainCards = useLevelUpStore.getState().setNewDomainCards;
  const setCardExchange = useLevelUpStore.getState().setCardExchange;
  const clearCardExchange = useLevelUpStore.getState().clearCardExchange;
  const validateCurrentStepFn = useLevelUpStore.getState().validateCurrentStep;
  const validateSession = useLevelUpStore.getState().validateSession;
  const commitLevelUp = useLevelUpStore.getState().commitLevelUp;
  const cancelWizard = useLevelUpStore.getState().cancelWizard;

  // Computed values
  const computed = useMemo(() => {
    if (!session || !character) {
      return {
        remainingSlots: 0,
        completionPercentage: 0,
        isComplete: false,
        hasTierAchievement: false,
        summary: null,
        currentStepName: 'Welcome',
        totalSteps: 6,
        canProceed: false
      };
    }

    const remainingSlots = getRemainingSlots(session);
    const completionPercentage = getWizardCompletionPercentage(session);
    const hasTierAchievement = !!session.tierAchievement;
    const summary = generateLevelUpSummary(session, character);

    // Step validation
    const validation = validateCurrentStepFn();
    const canProceed = validation.valid;

    return {
      remainingSlots,
      completionPercentage,
      isComplete: completionPercentage === 100,
      hasTierAchievement,
      summary,
      currentStepName: getStepName(currentStep),
      totalSteps: 6,
      canProceed
    };
  }, [session, character, currentStep]);

  return {
    // State
    session,
    currentStep,
    character,
    isLoading,
    errors,
    warnings,

    // Computed
    ...computed,

    // Actions
    initializeSession,
    loadExistingSession,
    clearStore,
    setCurrentStep,
    goNext,
    goBack,
    canGoNext,
    canGoBack,
    setTierAchievementExperience,
    addAdvancementChoice,
    removeAdvancementChoice,
    clearAdvancementChoices,
    setAdditionalDomainCard,
    setNewDomainCards,
    setCardExchange,
    clearCardExchange,
    validateCurrentStep: validateCurrentStepFn,
    validateSession,
    commitLevelUp,
    cancelWizard
  };
}

/**
 * Gets human-readable step name
 */
function getStepName(step: WizardStep): string {
  switch (step) {
    case WizardStep.WELCOME:
      return 'Welcome';
    case WizardStep.TIER_ACHIEVEMENT:
      return 'Tier Achievement';
    case WizardStep.ADVANCEMENTS:
      return 'Advancements';
    case WizardStep.DOMAIN_CARDS:
      return 'Domain Cards';
    case WizardStep.SUMMARY:
      return 'Summary';
    case WizardStep.COMPLETION:
      return 'Complete';
    default:
      return 'Unknown';
  }
}

/**
 * Hook for step-specific functionality
 * Returns only the data and actions needed for a specific wizard step
 */
export function useWizardStep(step: WizardStep) {
  const currentStep = useLevelUpStore((state) => state.currentStep);
  const isActive = currentStep === step;

  return {
    isActive,
    step
  };
}

/**
 * Hook for tier achievement step
 */
export function useTierAchievementStep() {
  const session = useLevelUpStore((state) => state.session);
  const setExperience = useLevelUpStore((state) => state.setTierAchievementExperience);
  const { isActive } = useWizardStep(WizardStep.TIER_ACHIEVEMENT);

  const tierAchievement = session?.tierAchievement;

  return {
    isActive,
    tierAchievement,
    setExperience,
    isComplete: !!tierAchievement?.newExperience
  };
}

/**
 * Hook for advancements step
 */
export function useAdvancementsStep() {
  const session = useLevelUpStore((state) => state.session);
  const character = useLevelUpStore((state) => state.character);
  const addChoice = useLevelUpStore((state) => state.addAdvancementChoice);
  const removeChoice = useLevelUpStore((state) => state.removeAdvancementChoice);
  const clearChoices = useLevelUpStore((state) => state.clearAdvancementChoices);
  const { isActive } = useWizardStep(WizardStep.ADVANCEMENTS);

  const choices = session?.advancementChoices || [];
  const remainingSlots = session ? getRemainingSlots(session) : 2;
  const tierState = session?.tierAdvancementState;

  return {
    isActive,
    choices,
    remainingSlots,
    tierState,
    character,
    addChoice,
    removeChoice,
    clearChoices,
    isComplete: remainingSlots === 0
  };
}

/**
 * Hook for domain cards step
 */
export function useDomainCardsStep() {
  const session = useLevelUpStore((state) => state.session);
  const character = useLevelUpStore((state) => state.character);
  const setNewCards = useLevelUpStore((state) => state.setNewDomainCards);
  const setExchange = useLevelUpStore((state) => state.setCardExchange);
  const clearExchange = useLevelUpStore((state) => state.clearCardExchange);
  const { isActive } = useWizardStep(WizardStep.DOMAIN_CARDS);

  const newDomainCards = session?.newDomainCards || [];
  const exchange = session?.domainCardExchange;
  const characterLevel = character ? parseInt(character.level) : 1;
  const requiredCardCount = session?.hasAdditionalDomainCard ? 2 : 1;

  return {
    isActive,
    newDomainCards,
    exchange,
    characterLevel,
    character,
    setNewCards,
    setExchange,
    clearExchange,
    isComplete: newDomainCards.length === requiredCardCount
  };
}

/**
 * Hook for summary step
 */
export function useSummaryStep() {
  const session = useLevelUpStore((state) => state.session);
  const character = useLevelUpStore((state) => state.character);
  const { isActive } = useWizardStep(WizardStep.SUMMARY);

  const summary = session && character
    ? generateLevelUpSummary(session, character)
    : null;

  return {
    isActive,
    session,
    character,
    summary,
    isComplete: true
  };
}

/**
 * Hook for navigation controls
 */
export function useWizardNavigation() {
  const currentStep = useLevelUpStore((state) => state.currentStep);
  const session = useLevelUpStore((state) => state.session);
  const canGoNextFn = useLevelUpStore((state) => state.canGoNext);
  const canGoBackFn = useLevelUpStore((state) => state.canGoBack);
  const goNext = useLevelUpStore((state) => state.goNext);
  const goBack = useLevelUpStore((state) => state.goBack);
  const validateCurrentStep = useLevelUpStore((state) => state.validateCurrentStep);

  // Memoize validation and navigation checks to prevent infinite loops
  const computed = useMemo(() => {
    const validation = validateCurrentStep();
    const canProceed = validation.valid && canGoNextFn();
    const canGoBackValue = canGoBackFn();

    return {
      validation,
      canProceed,
      canGoBackValue
    };
  }, [currentStep, session, validateCurrentStep, canGoNextFn, canGoBackFn]);

  const nextLabel = useMemo(() => {
    if (currentStep === WizardStep.SUMMARY) return 'Confirm & Complete';
    if (currentStep === WizardStep.COMPLETION) return 'Close';
    return 'Next';
  }, [currentStep]);

  return {
    currentStep,
    canGoNext: computed.canProceed,
    canGoBack: computed.canGoBackValue,
    goNext,
    goBack,
    nextLabel,
    backLabel: 'Back',
    validation: computed.validation,
    isCompletionStep: currentStep === WizardStep.COMPLETION
  };
}

/**
 * Hook for progress indicator
 */
export function useWizardProgress() {
  const session = useLevelUpStore((state) => state.session);
  const currentStep = useLevelUpStore((state) => state.currentStep);
  const character = useLevelUpStore((state) => state.character);

  const completionPercentage = session
    ? getWizardCompletionPercentage(session)
    : 0;

  // Calculate which steps are complete
  const stepsComplete = useMemo(() => {
    if (!session) return {};

    const requiredCardCount = session.hasAdditionalDomainCard ? 2 : 1;
    const domainCardsComplete = session.newDomainCards && session.newDomainCards.length === requiredCardCount;

    return {
      welcome: currentStep > WizardStep.WELCOME,
      tierAchievement: !session.tierAchievement || !!session.tierAchievement.newExperience,
      advancements: getRemainingSlots(session) === 0,
      domainCards: domainCardsComplete,
      summary: currentStep > WizardStep.SUMMARY
    };
  }, [session, currentStep]);

  return {
    currentStep,
    completionPercentage,
    stepsComplete,
    stepName: getStepName(currentStep),
    totalSteps: 6,
    currentStepNumber: currentStep + 1
  };
}

/**
 * Hook for error handling
 */
export function useWizardErrors() {
  const errors = useLevelUpStore((state) => state.errors);
  const warnings = useLevelUpStore((state) => state.warnings);

  return {
    errors,
    warnings,
    hasErrors: errors.length > 0,
    hasWarnings: warnings.length > 0
  };
}
